import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { personService } from "@shared/services/personService";
import { registerService } from "@shared/services/register";
import { useNavigate } from "react-router-dom";
import { useReportGenerator } from "@shared/hooks/useReportGenerator";
import { useTranslation } from "react-i18next";

// PDF report columns: full names per spec, pdfMake wraps text automatically
const PDF_REPORT_COLUMNS = [
    {
        label: 'Партида',
        children: [
            { key: 'fromDate', label: 'от дата' },
            { key: 'parNumber', label: '№ партида' },
            { key: 'forEmployer', label: 'за работодател' },
            { key: 'createdByUser', label: 'създадена от потребител' },
        ]
    },
    {
        label: 'Електронен трудов запис',
        children: [
            { key: 'correctionCode', label: 'код корекция' },
            { key: 'documentType', label: 'тип документ' },
            { key: 'documentTypeName', label: 'наименование на тип документ' },
        ]
    },
    { key: 'employerEik', label: 'ЕИК на работодателя / органа по назначаването' },
    { key: 'idn', label: 'ЕГН/ЛН,ЛНЧ/' },
    { key: 'idnCode', label: 'Код за ЕГН /ЛН, ЛНЧ/ Сл. номер от НАП' },
    { key: 'fullName', label: 'Име Презиме Фамилия' },
    { key: 'contractDate', label: 'Дата на сключване на трудовия договор или на акта за назначаване' },
    { key: 'appointmentActNo', label: 'Номер на акта за назначаване' },
    {
        label: 'Основание на трудовия договор/акта за назначаване или основание за промяна на правоотношението',
        children: [
            { key: 'reasonCode', label: 'код' },
            { key: 'reasonName', label: 'наименование' },
        ]
    },
    { key: 'effectiveDate', label: 'Дата на начало на изпълнение на трудовия договор или на възникване на служебното правоотношение' },
    { key: 'contractTerm', label: 'Срок на договора (на допълнителното споразумение) или на акта за назначаване' },
    {
        label: 'Допълнително споразумение или издаване на акта за промяна служебното правоотношение',
        children: [
            { key: 'supplAgreeDate', label: 'Дата на сключване' },
            { key: 'effectiveDateChange', label: 'Дата на влизане в сила' },
            { key: 'changeActNo', label: 'Номер на акта за промяна' },
        ]
    },
    { key: 'salary', label: 'Размер на основното трудово възнаграждение или на основната месечна заплата' },
    { key: 'annualLeaveAgreed', label: 'Дни уговорен платен годишен отпуск по трудовото правоотношение' },
    { key: 'annualLeaveUsed', label: 'Дни използван платен годишен отпуск полагащ се за годината на прекратяване на трудовото правоотношение' },
    {
        label: 'НКПД',
        children: [
            { key: 'nkpdCode', label: 'код' },
            { key: 'nkpdName', label: 'наименование' },
        ]
    },
    {
        label: 'КИД',
        children: [
            { key: 'nkidCode', label: 'код' },
            { key: 'nkidName', label: 'наименование' },
        ]
    },
    {
        label: 'ЕКАТТЕ',
        children: [
            { key: 'ekatteCode', label: 'код' },
            { key: 'ekattePlace', label: 'населено място' },
        ]
    },
    { key: 'workTimeDuration', label: 'Продължителност на работното време' },
    { key: 'terminationDate', label: 'Дата на прекратяване на правоотношението' },
    {
        label: 'Основание за прекратяване',
        children: [
            { key: 'terminationReasonCode', label: 'код' },
            { key: 'terminationReasonName', label: 'наименование' },
        ]
    },
    { key: 'compensation222_2Date', label: 'Изплатено обезщетение по чл. 222, ал. 2 от КТ при прекратяване на трудовия договор (дата)' },
    { key: 'compensation222_3Date', label: 'Изплатено обезщетение по чл. 222, ал. 3 от КТ при прекратяване на трудовия договор или изплатените обезщетения при прекратяване на служебното правоотношение по чл. 106, ал. 3 от ЗДСл (дата)' },
    { key: 'workExperienceRecognized', label: 'Продължителност на времето, което се признава за трудов стаж (ггммдд)' },
    { key: 'workExperienceNotRecognized', label: 'Продължителност на времето, което НЕ се признава за трудов стаж (ггммдд)' },
    { key: 'blockedMessages', label: 'Запорни съобщения' },
];

// noiCode labels: 0 – Редовни данни, 1 – Корекция, 2 – Заличаване, null – Не уведомява НАП
function noiCodeToLabel(noiCode) {
    if (noiCode == null) return 'Не уведомява НАП';
    switch (Number(noiCode)) {
        case 0: return 'Код корекция за НОИ';
        case 1: return 'Корекция';
        case 2: return 'Заличаване';
        default: return String(noiCode);
    }
}

// Maps API response fields to PDF report column keys
function mapApiRecordToReportRow(rec) {
    if (!rec) return null;
    const v = (x) => (x != null && x !== '' ? String(x) : '');
    const blocked = rec.blockedSalaryAmount;
    const blockedVal = typeof blocked === 'boolean' ? (blocked ? '1' : '0') : v(blocked);
    return {
        fromDate: v(rec.parFromDate),
        parNumber: v(rec.parId),
        forEmployer: v(rec.branchName),
        createdByUser: v(rec.userCreatedFullName),
        correctionCode: noiCodeToLabel(rec.noiCode),
        documentType: v(rec.typeDoc),
        documentTypeName: v(rec.typeDocName),
        employerEik: v(rec.bulstat),
        idn: v(rec.idn),
        idnCode: v(rec.identType),
        fullName: v(rec.personFullName),
        contractDate: v(rec.contractDate),
        appointmentActNo: v(rec.contractNo),
        reasonCode: v(rec.reasonCode),
        reasonName: v(rec.reasonName),
        effectiveDate: v(rec.effectiveDate),
        contractTerm: '',
        supplAgreeDate: '',
        effectiveDateChange: '',
        changeActNo: '',
        salary: v(rec.salary),
        annualLeaveAgreed: v(rec.daysAgreedPaidAnnualLeave),
        annualLeaveUsed: v(rec.usedVacationDays),
        nkpdCode: v(rec.nkpdCode),
        nkpdName: v(rec.nkpdName),
        nkidCode: v(rec.nkidCode),
        nkidName: v(rec.nkidName),
        ekatteCode: v(rec.ekkateCode),
        ekattePlace: v(rec.ekkateLocation),
        workTimeDuration: v(rec.durationDays),
        terminationDate: v(rec.breakDateContract),
        terminationReasonCode: v(rec.reasonLeaveCode),
        terminationReasonName: v(rec.reasonLeaveName),
        compensation222_2Date: v(rec.datePaidCompensation222_2),
        compensation222_3Date: v(rec.datePaidCompensation222_3),
        workExperienceRecognized: v(rec.recognizedWorkExp),
        workExperienceNotRecognized: v(rec.notRecognizedWorkExp),
        blockedMessages: blockedVal,
    };
}

export default function ElectronicWorkReportTable() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [empBranch, setEmpBranch] = useState([]);
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [searchIdn, setSearchIdn] = useState("");
    const [searchName, setSearchName] = useState("");

    const {
        generatePdfReport: handleExecuteReport,
        pdfLoading: reportPdfLoading,
    } = useReportGenerator({
        title: 'Справка - Уведомления по чл. 62, ал. 5 от Кодекса на труда за периода',
        columns: PDF_REPORT_COLUMNS,
        fileName: 'electronic_work_report',
        companyName: '',
        reportService: selectedEmp
            ? async () => {
                const res = await registerService.getEmpContractsReport(selectedEmp.id);
                const raw = Array.isArray(res) ? res : (res?.content ?? res?.data ?? (res && typeof res === 'object' ? [res] : []));
                const rows = raw.map(mapApiRecordToReportRow).filter(Boolean);
                if (rows.length === 0) {
                    throw new Error('Няма данни за отчет за този служител');
                }
                return rows;
            }
            : null,
        customStyles: {
            rowNumberLabel: '№ ред',
            tableFontSize: '5px',
            pageSize: 'A2',
            pageOrientation: 'landscape',
            pageMargins: [15, 60, 15, 35],
            tableHeaderBgColor: '#28a745',
            tableHeaderTextColor: '#ffffff',
            preparedBy: selectedEmp?.fullName,
        },
    });

    const handleEmpRowClick = (emp) => {
        setSelectedEmp(prev => prev?.id === emp.id ? null : emp);
    };

    const handleGoBack = () => {
        navigate("/personnel/employment-contracts/electronic-work");
    };

    const loadEmpBranch = async (params = {}) => {
        try {
            const res = await personService.getEmpBranch(params);
            setEmpBranch(res?.content ?? []);
        } catch (err) {
            console.error("Error fetching emp-branch:", err);
        }
    };

    const handleSearch = () => {
        loadEmpBranch({ idn: searchIdn, name: searchName });
    };

    useEffect(() => {
        loadEmpBranch();
    }, []);

    return (
        <div className="flex-column width-100 gap-20">
            <h1 className="title font-size-24 span-bold">{t('staff.register.electronicWorkReport.title')}</h1>
            <div className="flex-column width-65" style={{ margin: "0 auto" }}>
                <div className="flex-column border-radius-black overflow-auto padding-10">
                    <span className="background-color-white-blue">{t('staff.register.electronicWorkReport.type')}</span>
                    <div className="flex-row">
                        <input type="radio" name="reportType" id="reportType1" />
                        <span>{t('staff.register.electronicWorkReport.sent')}</span>
                        <input type="radio" name="reportType" id="reportType1" />
                        <span>{t('staff.register.electronicWorkReport.notSent')}</span>
                        <input type="radio" name="reportType" id="reportType1" />
                        <span>{t('staff.register.electronicWorkReport.notConfirmed')}</span>
                        <input type="radio" name="reportType" id="reportType1" />
                        <span>{t('staff.register.electronicWorkReport.confirmed')}</span>
                        <input type="radio" name="reportType" id="reportType1" />
                        <span>{t('staff.register.electronicWorkReport.rejected')}</span>
                        <input type="radio" name="reportType" id="reportType1" />
                        <span>{t('staff.register.electronicWorkReport.inactive')}</span>
                        <input type="radio" name="reportType" id="reportType1" />
                        <span>{t('staff.register.electronicWorkReport.all')}</span>
                    </div>
                </div>
                <div className="flex-column border-radius-black padding-10">
                    <span className="background-color-white-blue">{t('staff.register.electronicWorkReport.emplooyees')}</span>
                    <div className="flex-row">
                        <span>{t('staff.register.electronicWorkReport.idn')}</span>
                        <input
                            className="small-input width-20"
                            type="text"
                            placeholder="Enter IDN"
                            value={searchIdn}
                            onChange={(e) => setSearchIdn(e.target.value)}
                        />
                        <span>{t('staff.register.electronicWorkReport.employeeName')}</span>
                        <input
                            className="small-input width-40"
                            type="text"
                            placeholder="Enter Employee name"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                        />
                        <button className="form-btn btn-blue" onClick={handleSearch}>Search</button>
                    </div>
                </div>
                <div className="flex-column border-radius-black padding-10">
                    <div className="flex-column width-50 electronic-work-emp-list" style={{ margin: "0 auto" }}>
                        <span className="text-center">---{t('staff.register.electronicWorkReport.allEmployees')}---</span>
                        {empBranch.map((emp) => (
                            <div
                                key={emp.id}
                                className={`flex-row ${selectedEmp?.id === emp.id ? 'record-selected' : ''}`}
                                onClick={() => handleEmpRowClick(emp)}
                                style={{ cursor: 'pointer' }}
                            >
                                <span>{emp.idn}</span>
                                <span>-</span>
                                <span className="no-wrap">{emp.fullName}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex-row justify-space-between border-radius-black padding-10">
                    <div className="flex-row">
                        <input type="checkbox" name="selectAll" id="selectAll" />
                        <span>{t('staff.register.electronicWorkReport.saveReport')}</span>
                    </div>
                    <div className="flex-row">
                        <span>{t('staff.register.electronicWorkReport.from')}:</span>
                        <input className="small-input width-40" type="date" placeholder="Enter From date" />
                        <span>{t('staff.register.electronicWorkReport.to')}:</span>
                        <input className="small-input width-40" type="date" placeholder="Enter To date" />
                    </div>
                </div>
                <div className="flex-row justify-center border-radius-black padding-10 gap-10">
                    <button
                        className="form-btn btn-blue width-20"
                        onClick={handleExecuteReport}
                        disabled={reportPdfLoading || !selectedEmp}
                    >
                        {reportPdfLoading ? (
                            <span><FontAwesomeIcon icon={faSpinner} spin /> Генериране...</span>
                        ) : (
                            t('staff.register.electronicWorkReport.execute')
                        )}
                    </button>
                    <button className="form-btn btn-red width-10" onClick={handleGoBack}>{t('common.exit')}</button>
                </div>
            </div>
        </div>
    )
}